import React from 'react';
import { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Link, Container, Alert, Divider, Button, Box, Typography, Grid } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
//
import { AppContext } from '/@/app/AppContext';
import { ScrollContext } from '/@/app/AppLayout';
import { Linkify } from '/@/app/linkify';
import { route } from '/@/app/routes';
import { StepInfoBlock } from '/@/app/components/StepInfoBlock';
import { LifeExpectancyChart } from '/@/app/components/LifeExpectancyChart';
import { AudioPlayer } from '/@/app/components/AudioPlayer';
import { findLifeExpectancy } from '/@/app/views/RisksView/lifeExpectancyFilter';
import { userFlowState } from '/@/app/states/userFlowState';
import { AgeAssessmentModalState } from '/@/app/views/AgeAssessmentView/AgeAssessmentModalState';
import { AgeAssessmentModal } from '/@/app/views/AgeAssessmentView/AgeAssessmentModal';
import { PaywallBlock, ExpireBlock } from '/@/app/components/Paywall'
import { UpdateAnnouncements } from '/@/app/components/UpdateAnnouncements';
import { useApi} from '/@/app/Api';



const Item = styled(Box)(({ theme }) => ({
    backgroundColor: '#f5f7fb',
    ...theme.typography.body2,

    borderRadius: theme.shape.borderRadius * 1.5,
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(4.5),
    },
    //
    textAlign: "left",
    color: theme.palette.text.primary,
}));


const Age = ({ age, modalState }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    let hidden_by_plan = false;
    const { featureEnabled } = useContext(AppContext);
    const { payment_system_enabled } = useContext(AppContext).bootstrapData;
    if (
        age.requires_feature
        && !featureEnabled(age.requires_feature)  // fixme simplify
    ) {
        hidden_by_plan = true;
    }

    return (
        <>
            <AudioPlayer audioSrc={age.sound_link} sx={{ position: "absolute", top: "10px", right: "10px" }} size="25px" hideCaption={true} />
            <Typography variant="h5" component="h3" gutterBottom>
		{age.sponsorship && <WorkspacePremiumIcon sx={{ mr: 1,color:'green' }} titleAccess={t('Доступно только для спонсоров')}/>}
                {age.caption}
            </Typography>

            {age.age &&
                <Typography variant="h4" sx={{
                    color: hidden_by_plan ? "lightgrey" : theme.palette.warning.main,

                }}>
                    {age.age} {age.units}
                </Typography>
            }

            {hidden_by_plan ?
                <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                    {!payment_system_enabled
                        ? <>
                            Показывается в том случае, если не ранее, чем 12 месяцев назад,
                            были импортированы результаты анализов из партнёрских лабораторий ДНКОМ,
                            Аква-лаб и др.
                        </>
                        : <>
                            Показывается при покупке подписки или при статусе спонсора.
                        </>
                    }
                </Alert>
                :
                <>
                    {/* Comments should always show: independently of age value */}
                    <Typography variant="body1">
                        <Linkify text={age.comments} />
                    </Typography>

                    {(age.age && age.action_plan) &&
                        <>
                            <Divider sx={{ mt: 2, mb: 2 }} />
                            <Button variant="outlined" onClick={() => modalState.show(age)}>{t("План действий")}</Button>
                        </>
                    }
                    {(
                            age.age
                            &&
                            (
                                /* For specific item, find by name; FIXME */
                                age.caption?.toLowerCase().includes("фенотипический возраст")
                                || age.caption?.toLowerCase().includes("биологический возраст)")
                                || age.caption?.toLowerCase().includes("phenotypic age")
                                || age.caption?.toLowerCase().includes("biological age)")
                            )) &&
                        <Box sx={{ textAlign: "center", mt: 2 }}>
                            <Link
                                component={ReactRouterLink}
                                to={route("/age/calculator/")}
                                sx={{ display: "flex", flexFlow: "row nowrap", justifyContent: "center", alignItems: "center" }}
                            >
                                <CalculateIcon sx={{ mr: 1 }}/>{t("Калькулятор возраста")}
                            </Link>
                        </Box>
                    }
                </>
            }
        </>
    )
}

export const AgeAssessmentView = () => {
    const api=useApi();
    const { t,i18n } = useTranslation();
const {restoreScroll}=useContext(ScrollContext);
    const { bootstrapData } = useContext(AppContext);
    const { help } = useContext(AppContext).bootstrapData;
    const [ages,setAges] =useState(null);
    const [meta,setMeta] =useState(null);

    const modalState = new AgeAssessmentModalState();

    let lifeExpectancyExists = !!bootstrapData.panel.life_expectancy_by_plan;

    userFlowState.markAgesVisited();

useEffect(() =>
{
	api.request('cabinet/ages',{panel_id:bootstrapData.panel.id},{api_url:'v2'})
	.then(re=>{ setMeta(re[0]); setAges(re.slice(1)); });
}, []);

useEffect(() =>
{
	if (!ages) return;
	restoreScroll();
}, [ages]);

if (!ages) return (<></>);

    return (
        <Container maxWidth="sm">
            <Box>
                <AudioPlayer audioSrc={help.sounds['ages-'+i18n.language]} sx={{ ml: 2, mb: 1, mr: 1, float: "right" }} />
                <Typography variant="h3" gutterBottom>{t("Оценка возраста")}</Typography>
            </Box>
            <Container maxWidth="sm" className="home-view-centered-content">
                <UpdateAnnouncements mode="silent"/>
            </Container>

            {userFlowState.globalPanel.last_calculated_at !== null &&
                <>
                    {ages.length === 0 && !lifeExpectancyExists ?
			<Alert severity="warning">
			    {t("flow-warning-answer")} <ReactRouterLink to={route("/questions/")}>{t("вопросы")}</ReactRouterLink>{" "}
			    {t("и заполните")} <ReactRouterLink to={route("/lab-tests/")}>{t("данные анализов")}</ReactRouterLink>,
			    {t("чтобы узнать возраст систем вашего организма")}.
			</Alert>
                        :
                        <Grid container spacing={2}>
                            {lifeExpectancyExists &&
                                <>
                                    <Grid item xs={12} key="caption">
                                        <Typography variant="h5" component="h3" sx={{textAlign: "center", width: "100%", mt: 2}}>{t("Продолжительность жизни, годы")}:</Typography>
                                    </Grid>
                                    <Grid item xs={12} key="chart">
                                        <LifeExpectancyChart/>
                                    </Grid>
                                </>
                            }
                            {meta.life_expectancy_text &&
                                <Grid item xs={12} key="lifeExpectancyText" sx={{mt: 2}}>
                                    <Typography variant="h6" component="h4" gutterBottom sx={{
                                        color: "black",
                                        fontWeight: "normal",
                                        lineHeight: "1.25",  // 1.25 seems visually good for multiline header
                                        marginBottom: "15px",
                                    }}>
                                        <Linkify text={meta.life_expectancy_text}/>
                                    </Typography>
                                </Grid>
                            }

                            {ages.map((age, index) => (
				age.expire_block ?  (<ExpireBlock data={age}/>) :(
                                <Grid item xs={12} key={index}>
                                    <Item sx={{
                                        color: age.age === "" ? "gray" : "inherit",
                                        textAlign: "center",
                                        position: "relative"
                                    }}>
                                        { age.paywall_block?<PaywallBlock data={age}/>:<Age age={age} modalState={modalState}/> }
                                    </Item>
                                </Grid>
				)
                            ))}
                        </Grid>
                    }
                </>
            }
            <StepInfoBlock text={ help.texts['age-footer-'+i18n.language] } />
            <AgeAssessmentModal modalState={modalState} />
        </Container>
    );
}
